import { ChangeEvent, useState } from 'react';
import { Button, Row, Col, } from 'react-bootstrap';
import { useField } from 'formik';
import { AttachmentDto } from '../../../api/web-api-client';
import { formatBytes, formatDateToString } from '../../../utils';
import ConfirmationModal from '../../modals/ConfirmationModal';
import SelectInput from '../SelectInput';
import { SelectInputOption } from '../SelectInput/types';
import { getFileUrlFromBase64 } from '../../../routes/common/helperFunctions';

interface AttachmentItemProps {
    id?: string;
    name: string,
    index: number,
    canRemove: boolean;
    canUpload: boolean;
    cancelButtonId: string;
    isSummary: boolean;
    onRemoveItem?: (attachment: AttachmentDto) => Promise<void>;
    onCategoryUpdate?: (docId: string, category: string) => Promise<void>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fileBytes?: any;
    isSubmitted?: boolean;
}

const AttachmentItemNew = ({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    canRemove, onRemoveItem, canUpload, cancelButtonId, isSummary, name, index, fileBytes, onCategoryUpdate, id, isSubmitted,
} : AttachmentItemProps) => {
    const getNameForUse = (fieldName: string) => {
        const field = `${name}[${index}].${fieldName}`;
        return field;
    }
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [catField, catMeta, { setValue, setTouched }] = useField({ name: getNameForUse('attachmentCategory') });
    const [contentField] = useField<AttachmentDto>({ name: `${name}[${index}]` });

    const dto: AttachmentDto = contentField.value;
    const author = dto.attachmentAuthor;
    const portalUser = dto.attachmentPortalUsername;
    // const createdDate = dto.attachmentCreatedDate;
    // const uploadedDate = dto.uploadDate;
    const modifiedDate = dto.attachmentModifiedDate;
    const isPublished = dto.attachmentPublishedStatus === 'Published';
    const isOriginNMI = dto.attachmentOrigin === 'Pattern Approval';

    const closeModal = () => setDeleteDialogOpen(false);
    const showModal = () => setDeleteDialogOpen(true);
    const fileUrl = fileBytes && getFileUrlFromBase64(fileBytes);

    /* TO DO - Add in Category select input */
    const getCategories = () => {
        const options: SelectInputOption<string>[] = [];
        options.push({ displayText: 'Test Report', value: 'Test Report' });
        options.push({ displayText: 'Certificate', value: 'Certificate' });
        options.push({ displayText: 'Specification', value: 'Specification' });
        options.push({ displayText: 'Manual', value: 'Manual' });
        options.push({ displayText: 'Photo or diagram', value: 'Photo or Diagram' });
        options.push({ displayText: 'Other', value: 'Other' });
        return options;
    };

    const onCategoryChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.value !== null) {
            setValue(event.target.value);
            setTouched(true);
            if (onCategoryUpdate && id) {
                onCategoryUpdate(id, event.target.value);
            }
        }
    };

    return (
        <div className='attachment mb-2'>
            {/* Note: This UI render template is a variation of ProgressFileList.tsx UI render */}
            <Row className='row-cols-2 row-cols-sm-2 row-cols-md-4 -align-items-center align-items-top'>
                <Col xs={{ span: 10, order: 1 }} sm={{ span: 11, order: 1 }} md={{ span: 5, order: 1 }} className='d-flex -flex-fill -align-items-center pt-1'>
                    {/* Icon and filename (flex: 1 for most space) */}
                    <i className='icon-file fs-2 me-2' aria-hidden='true' />
                    <div className='attachment-description -d-flex -flex-column mb-2 ms-1 me-2 -w-100'>
                        {/* WIP
                        {isSummary ? (
                            <span className='attachment-name pt-0 text-left text-break'>
                                {contentField.value.attachmentName}
                            </span>
                        ) : ( */}
                            <a
                                href={fileUrl}
                                download={contentField.value.attachmentName}
                                className='attachment-name pt-0 text-left text-break'
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                {contentField.value.attachmentName}
                            </a>
                        {/* )} */}
                        {(isPublished && isOriginNMI) && (
                          <span className='badge badge-sm rounded-pill bg-danger ms-2 small' title='Published by NMI'>NMI</span>
                        )}
                        <br />
                        <span className='attachment-size mt-1 fs-8 fw-normal text-left'>
                            {(contentField.value.attachmentSize) ? formatBytes(+(contentField.value.attachmentSize)) : null}
                        </span>
                    </div>
                </Col>
                <Col xs={{ span: 10, offset: 1, order: 3 }} sm={{ offset: 1, order: 3 }} md={{ offset: 0, order: 2 }} className='attachment-cat fw-normal small lh-sm'>
                  {/* Dropdown (fixed width, margin for spacing) */}
                  <div style={{ minWidth: '205px'}}> 
                      <SelectInput<string>
                          id={catField.name}
                          key={catField.name}
                          name={catField.name}
                          label='Category'
                          defaultDisplayText='Select'
                          options={getCategories()}
                          onChange={onCategoryChange}
                          isSummary={isSummary}
                          containerClassName='me-4 pe-2 small'
                          className={`form-select-sm ${!catField.value && catMeta.touched ? 'is-invalid' : ''}`}
                          addBlank
                      />
                  </div>
                </Col>
                <Col xs={{ span: 10, offset: 1, order: 4 }} sm={{ offset: 1, order: 4 }} md={{ offset: 0, order: 3 }} className='attachment-mod fw-normal small lh-sm'>
                    {/* Only visible once submitted and cannot be removed */}
                    {(isSubmitted && !canRemove) && (
                      <div className='mt-1'>
                        {isOriginNMI ? author : portalUser}<br/>
                        {formatDateToString(modifiedDate)}
                      </div>
                    )}
                </Col>
                {/* Remove button (auto width, margin for spacing) */}
                {canRemove && (
                    <Col xs={{ span: 1, order: 2 }} sm={{ span: 1, order: 2 }} md={{ span: 1, order: 4 }} className='-d-none -d-md-flex d-flex -align-items-center justify-content-end'>
                        <Button
                            variant='btn btn-flat'
                            data-testid={cancelButtonId}
                            id={cancelButtonId}
                            name={cancelButtonId}
                            title='Delete'
                            aria-label='Delete'
                            className='d-flex align-self-top mt-2 p-0 fs-7'
                            onClick={showModal}
                        >
                            <i className='icon-close' aria-hidden='true' />
                            <span className='visually-hidden'>{`Delete ${contentField.value.attachmentName}`}</span>
                        </Button>
                    </Col>
                )}
                {canRemove && (
                    <ConfirmationModal
                        closeModal={closeModal}
                        isOpen={deleteDialogOpen}
                        titleText='Confirm deletion'
                        bodyText='Are you sure you want to remove this file?'
                        onModalNo={closeModal}
                        onModalYes={() => onRemoveItem && onRemoveItem(contentField.value)}
                        noButtonTitle='Cancel'
                        yesButtonTitle='Yes, delete'
                    />
                )}
            </Row>
            {/* Reserve space for error message so layout doesn't shift */}
            {!isSummary && (
                <div style={{ minHeight: 20 }}>
                    {!catField.value && catMeta.touched && (
                        <div className='text-danger text-body'>
                            Select a category that best describes this document.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AttachmentItemNew;
